import { useCallback, useEffect, useMemo, useState } from 'react';
import { Point } from 'geojson';
import { MapLayerProps } from 'features/mapping/mapping.types';
import { Layer, Map, MapRef, useMapInstance } from 'shared/map-exports';
import { useMapEvent } from 'features/mapping/hooks/useMapEvent';
import { addVisible } from 'shared/addVisible';
import { VisibilityToggledSource } from 'features/mapping/components/layers/VisibilityToggledSource';
import { getWatchdutyTileUrl } from 'features/mapping/components/layers/layers.utils';
import {
  COUNTS_MIN_ZOOM,
  DEFAULT_LIGHTNING_LOOKBACK,
  HEATMAP_LAYER_ID,
  HEATMAP_MAX_ZOOM,
  ITEM_ICON_LAYER_ID,
  ITEM_MIN_ZOOM,
  ITEM_SELECTED_LAYER_ID,
  REFRESH_INTERVAL_SECONDS,
  SELECTED_PULSE_BASE_RADIUS,
  SOURCE_ID,
  SOURCE_LAYER,
} from 'features/mapping/components/layers/LightningLayer/LightningLayer.constants';
import {
  getLightningTimeFilter,
  getSelectedLightningId,
} from 'features/mapping/components/layers/LightningLayer/LightningLayer.utils';
import {
  getHeatmapStyle,
  getItemIconStyle,
  getSelectedPulseStyle,
  getTrailingWindowFilter,
} from 'features/mapping/components/layers/LightningLayer/LightningLayer.styles';
import {
  BELOW_STREETS_LAYER_ID,
  ON_TOP_OF_EVERYTHING_LAYER_ID,
} from 'features/mapping/styles';
import { usePulseAnimation } from 'hooks/usePulseAnimation';
import { useTimelineClockValue } from 'features/mapping/state/useTimelineClockState';
import { usePoisState } from 'state/usePoisState';
import { useMapMode } from 'hooks/useMapMode';
import { useIsLightningToolsVisible } from 'features/mapping/hooks/useIsLightningToolsVisible';
import { useLightningToolsState } from 'state/useLightningToolsState';
import { useLightningToolsCommandPaletteAction } from 'hooks/commandPaletteActions';
import { useLightningLayerEnabled } from 'features/mapping/hooks/useLightningLayerEnabled';
import { useLightningTimelineLookbackHours } from 'features/mapping/hooks/useLightningTimelineLookbackHours';
import { LightningToolsCard } from './LightningToolsCard';
import { LightningLookback, StrikeCounts } from './LightningLayer.types';

type LightningLayerProps = { allowLightningControl: boolean } & MapLayerProps;

export const LightningLayer = (props: LightningLayerProps): JSX.Element => {
  const { allowLightningControl, visible } = props;
  const { showInternalLightningLayer } = useLightningLayerEnabled();
  const timelineLookbackHours = useLightningTimelineLookbackHours();
  const timelineClock = useTimelineClockValue();
  const { mapMode, isTimelineMode } = useMapMode();
  const isToolsVisible = useIsLightningToolsVisible();
  const { close } = useLightningToolsState();
  useLightningToolsCommandPaletteAction(allowLightningControl);
  const { selectedPoi } = usePoisState();
  const selectedId = getSelectedLightningId(selectedPoi);
  const mapInstance = useMapInstance();

  const [counts, setCounts] = useState<StrikeCounts | null>(null);
  const [countsLoading, setCountsLoading] = useState(false);
  const [selectedLookback, setSelectedLookback] = useState<LightningLookback>(
    DEFAULT_LIGHTNING_LOOKBACK,
  );

  // Override the selected lookback in timeline mode (card is hidden)
  const lookback = isTimelineMode
    ? DEFAULT_LIGHTNING_LOOKBACK
    : selectedLookback;

  const tileAgeRange =
    mapMode === 'lightning'
      ? { minAge: 0, maxAge: timelineLookbackHours * 60 }
      : lookback;

  // Counts only feed the tools card (hidden in timeline mode); otherwise every
  // frame's filter reload would recount
  const isCountingEnabled = visible && !isTimelineMode;

  // Reset the lookback to the default when closing the tools card
  const handleClose = useCallback(() => {
    setSelectedLookback(DEFAULT_LIGHTNING_LOOKBACK);
    close();
  }, [close]);

  const recomputeCounts = useCallback(() => {
    const map = mapInstance?.getMap();
    if (!map) return;

    const bounds = map.getBounds();
    if (!bounds) return;

    // Strike counts are hidden below COUNTS_MIN_ZOOM
    const currentZoom = map.getZoom();
    if (currentZoom < COUNTS_MIN_ZOOM) {
      setCounts(null);
      setCountsLoading(false);
      return;
    }

    const features = map.querySourceFeatures(SOURCE_ID, {
      sourceLayer: SOURCE_LAYER,
    });

    const seen = new Set<string | number>();
    const result = features.reduce(
      (acc, f) => {
        if (!f.id || seen.has(f.id)) return acc;
        const [lng, lat] = (f.geometry as Point).coordinates;
        if (!bounds.contains([lng, lat])) return acc;
        if (f.properties?.flash_polarity === 'P') acc.positive += 1;
        if (f.properties?.flash_polarity === 'N') acc.negative += 1;
        acc.total += 1;
        seen.add(f.id);
        return acc;
      },
      { positive: 0, negative: 0, total: 0 },
    );
    setCounts(result);
    setCountsLoading(false);
  }, [mapInstance]);

  // Show loading state for counts when the lookback value changes
  useEffect(() => {
    if (!isCountingEnabled) return;
    const map = mapInstance?.getMap();
    if (map && map.getZoom() >= COUNTS_MIN_ZOOM) setCountsLoading(true);
  }, [lookback, isCountingEnabled, mapInstance]);

  const recomputeIfSettled = useCallback(
    (map: MapRef | Map): void => {
      // Stands in for 'idle', which never fires while usePulseAnimation
      // continuously updates paint properties on a selected strike
      if (map.isMoving() || map.isZooming()) return;
      if (!map.isSourceLoaded(SOURCE_ID)) return;
      recomputeCounts();
    },
    [recomputeCounts],
  );

  // Reset counts when disabled / map unavailable, otherwise compute initial state.
  useEffect(() => {
    const map = mapInstance?.getMap();
    if (!map || !isCountingEnabled) {
      setCounts(null);
      setCountsLoading(false);
      return;
    }
    recomputeCounts();
  }, [mapInstance, isCountingEnabled, recomputeCounts]);

  // Show loading at the start of any pan/zoom; movestart covers both
  useMapEvent(
    'movestart',
    (event) => {
      if (event.target.getZoom() >= COUNTS_MIN_ZOOM) setCountsLoading(true);
    },
    mapInstance,
    isCountingEnabled,
  );

  // Recompute when the gesture settles or the source finishes loading
  useMapEvent(
    'moveend',
    (event) => recomputeIfSettled(event.target),
    mapInstance,
    isCountingEnabled,
  );
  useMapEvent(
    'sourcedata',
    (event) => {
      // Skip metadata events; they fire on URL swap while the old tile
      // pyramid still reports loaded, which would recount stale features
      if (event.sourceDataType === 'metadata') return;
      if (event.sourceId === SOURCE_ID) recomputeIfSettled(event.target);
    },
    mapInstance,
    isCountingEnabled,
  );

  usePulseAnimation({
    baseRadius: SELECTED_PULSE_BASE_RADIUS,
    isActive: visible && Boolean(selectedId),
    layerId: ITEM_SELECTED_LAYER_ID,
    map: mapInstance?.getMap(),
    maxZoomLevel: HEATMAP_MAX_ZOOM,
    minZoomLevel: ITEM_MIN_ZOOM,
  });

  const filter = useMemo(
    () =>
      getTrailingWindowFilter(
        getLightningTimeFilter(mapMode, timelineClock, Date.now()),
      ),
    [mapMode, timelineClock],
  );

  const heatmapStyle = getHeatmapStyle(filter);
  const itemIconStyle = getItemIconStyle(filter, showInternalLightningLayer);
  const selectedPulseStyle = getSelectedPulseStyle(filter, selectedId);

  return (
    <>
      <VisibilityToggledSource
        id={SOURCE_ID}
        url={getWatchdutyTileUrl(SOURCE_LAYER, {
          min_age_minutes: String(tileAgeRange.minAge),
          max_age_minutes: String(tileAgeRange.maxAge),
        })}
        refreshIntervalSeconds={REFRESH_INTERVAL_SECONDS}
        visible={visible}
      >
        <Layer
          id={HEATMAP_LAYER_ID}
          beforeId={BELOW_STREETS_LAYER_ID}
          {...addVisible(heatmapStyle, visible)}
        />
        <Layer
          id={ITEM_SELECTED_LAYER_ID}
          beforeId={ON_TOP_OF_EVERYTHING_LAYER_ID}
          {...addVisible(selectedPulseStyle, visible)}
        />
        <Layer
          id={ITEM_ICON_LAYER_ID}
          beforeId={ON_TOP_OF_EVERYTHING_LAYER_ID}
          metadata={{ interactive: showInternalLightningLayer }}
          {...addVisible(itemIconStyle, visible)}
        />
      </VisibilityToggledSource>
      {allowLightningControl && visible && (
        <LightningToolsCard
          open={isToolsVisible}
          counts={counts}
          countsLoading={countsLoading}
          lookback={lookback}
          onChangeLookback={setSelectedLookback}
          onClose={handleClose}
        />
      )}
    </>
  );
};
